<template>
    <div class="rejection-reasons-settings">
  
      <!-- add rejection reason button -->
      <div class="d-flex justify-space-between align-center flex-wrap my-4">
        <h3>  انواع العقارات</h3>
        <v-btn color="primary" @click="openAdd">
          <v-icon left>mdi-plus</v-icon>
          إضافة انواع جديدة
        </v-btn>
      </div>
  
      <!-- search and filter card -->
      <v-card class="mb-4">
        <v-card-text>
          <v-row align="center" dense class="filters-row">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="filters.search"
                label="بحث فى الانواع"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                clearable
                class="search-field"
                @input="onSearchInput"
                @click:clear="applyFilters"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- products table -->
      <table-component
        :showSelect="false"
        itemKey="id"
        :headers="headers"
        :pagination="pagination"
        :limit="$store.state.counterOfShow"
        :items="realEstateTypes"
        :loading="loading"
        v-model="page"
        @refresh="onTableRefresh"
      >

        <template v-slot:[`item.title`]="{ item }">
          <span class="value-cell" dir="ltr">{{ item.value }}</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center justify-center actions-cell">
            <v-btn
              color="blue darken-1"
              fab
              x-small
              dark
              depressed
              class="action-btn"
              @click="openEdit(item)"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>

            <v-btn
              color="error"
              fab
              x-small
              dark
              depressed
              class="action-btn"
              @click="openDelete(item)"
            >
              <v-icon small>mdi-trash-can-outline</v-icon>
            </v-btn>

          </div>
        </template>
      </table-component>
  
      <!-- real estate types form [add or edit] dialog -->
      <RealEstateTypesForm v-model="dialog" :edit-id="editId" @saved="fetchRealEstateTypes" />
  
      <!-- delete real estate type dialog -->
      <v-dialog v-model="deleteDialog" max-width="440" persistent>
        <v-card>
          <v-card-title class="headline">حذف انواع العقارات</v-card-title>
          <v-card-text>
            هل أنت متأكد من حذف «{{ deleteTarget?.value }}»؟
            
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="deleteDialog = false">إلغاء</v-btn>
            <v-btn color="error" :loading="deleting" @click="confirmDelete">تأكيد الحذف</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
    </div>
  </template>
  
  <script>
  import Axios from "axios";
  import { axiosInstance } from "@/axios/config";
  import RealEstateTypesForm from "./RealEstateTypesForm.vue";
  
  export default {
    name: "AdminRealEstateTypes",
    components: { RealEstateTypesForm },
    data() {
      return {
        loading: false,
        realEstateTypes: [],
        pagination: {
          limit: 10,
          count: 0,
        },
        page: 1,
        dialog: false,
        editId: null,
        deleteDialog: false,
        deleteTarget: null,
        deleting: false,
        searchTimer: null,
        filters: {
          search: "",
        },
        headers: [
          { text: "#", value: "id", width: "70px", sortable: false },
          { text: "الانواع", value: "value", sortable: false },
          { text: "العمليات", value: "actions", sortable: false, align: "center" },
        ],
      };
    },
    computed: {
      apiBase() {
        return `${this.$store.state.url}/employee/admin/real-estate-types`;
      },
    },
    watch: {
      page() {
        this.fetchRealEstateTypes();
      },
      "$store.state.counterOfShow"(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.fetchRealEstateTypes();
        }
      },
    },
    created() {
      this.fetchRealEstateTypes();
    },
    methods: {
      async fetchRealEstateTypes() {
        this.loading = true;
        try {
          const params = {
            page: this.page,
            per_page: this.$store.state.counterOfShow,
          };
          if (this.filters.search) params.search = this.filters.search;
          const res = await Axios.get(this.apiBase, {
            params,
            headers: this.request_headers(),
          });
          const { rows, total } = this.parseListPayload(res.data.payload || {});
          this.realEstateTypes = rows;
          this.pagination = {
            limit: this.$store.state.counterOfShow,
            count: total,
          };
        } catch (e) {
          this.showPopup("error", e.response?.data?.message || "تعذر تحميل قائمة الانواع");
        } finally {
          this.loading = false;
        }
      },
      onTableRefresh() {
        this.page = 1;
        this.fetchRealEstateTypes();
      },
      applyFilters() {
        if (this.page !== 1) {
          this.page = 1;
        } else {
          this.fetchRealEstateTypes();
        }
      },
      onSearchInput() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.applyFilters(), 500);
      },
      openAdd() {
        this.editId = null;
        this.dialog = true;
      },
      openEdit(realEstateType) {
        this.editId = realEstateType.id;
        this.dialog = true;
      },
      openDelete(realEstateType) {
        this.deleteTarget = realEstateType;
        this.deleteDialog = true;
      },
      async confirmDelete() {
        if (!this.deleteTarget) return;
        this.deleting = true;
        try {
          await Axios.delete(`${this.apiBase}/${this.deleteTarget.id}`, {
            headers: this.request_headers(),
          });
          this.showPopup("success", "تم الحذف بنجاح");
          this.deleteDialog = false;
          this.deleteTarget = null;
          if (this.realEstateTypes.length === 1 && this.page > 1) {
            this.page -= 1;
          } else {
            this.fetchRealEstateTypes();
          }
        } catch (e) {
          this.showPopup("error", e.response?.data?.message || "تعذر الحذف");
        } finally {
          this.deleting = false;
        }
      },
      parseListPayload(payload) {
        return {
          rows: payload.data || [],
          total: payload.data.length || 0,
        };
      },
    },
  };
  </script>
  
  <style scoped>
  .rejection-reasons-settings {
    padding: 16px;
  }
  
  .value-cell {
    font-family: monospace;
    font-size: 0.875rem;
    letter-spacing: 0.3px;
  }
  
  .image-cell {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .product-thumb {
    border-radius: 6px;
    border: 1px solid rgba(140, 140, 140, 0.3);
    background: rgba(0, 0, 0, 0.03);
  }
  
  .no-image-text {
    color: rgba(0, 0, 0, 0.38);
  }
  
  .theme--dark .product-thumb,
  .dark .product-thumb {
    border-color: rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.04);
  }
  
  .theme--dark .no-image-text,
  .dark .no-image-text {
    color: rgba(255, 255, 255, 0.45);
  }
  
  .status-chip {
    cursor: default;
  }
  .status-chip__icon {
    margin-inline-end: 6px;
  }
  
  .agents-sub {
    color: rgba(0, 0, 0, 0.54);
    font-size: 0.75rem;
  }
  
  .actions-cell {
    gap: 10px;
  }
  .action-btn {
    margin: 0;
  }
  
  .rejection-reasons-settings >>> .main__table .v-data-table td .v-btn--fab.v-size--x-small,
  .rejection-reasons-settings >>> .main__table .v-data-table td .site-show-btn {
    height: 24px;
    width: 24px;
  }
  .rejection-reasons-settings >>> .main__table .v-data-table td .v-btn--fab.v-size--x-small .v-icon {
    font-size: 15px;
  }
  
  .theme--dark .agents-sub,
  .dark .agents-sub {
    color: rgba(255, 255, 255, 0.6);
  }

  .role-filter__header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .role-filter__content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
  }

  .filters-row {
    margin-bottom: 0;
  }

  .search-field {
    margin-bottom: 0;
  }

  .role-filter__title {
    font-weight: 600;
    font-size: 0.95rem;
    color: rgba(0, 0, 0, 0.75);
  }

  .theme--dark .role-filter__title,
  .dark .role-filter__title {
    color: rgba(255, 255, 255, 0.87);
  }

  .role-radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    flex: 1;
    min-width: 0;
  }

  .role-radio-label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    padding: 8px 16px;
    border: 2px solid #3f0e40;
    border-radius: 999px;
    background-color: #fff;
    transition: all 0.2s ease;
    min-height: 38px;
  }

  .role-radio-label:hover {
    background-color: #f8f5f9;
    border-color: #5a2d5b;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(63, 14, 64, 0.12);
  }

  .role-radio-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .role-radio-text {
    font-size: 0.875rem;
    color: #3f0e40;
    font-weight: 500;
    user-select: none;
    pointer-events: none;
    white-space: nowrap;
  }

  .role-radio-label--active,
  .role-radio-label:has(.role-radio-input:checked) {
    background-color: #3f0e40;
    border-color: #3f0e40;
    box-shadow: 0 2px 6px rgba(63, 14, 64, 0.25);
  }

  .role-radio-label--active .role-radio-text,
  .role-radio-label:has(.role-radio-input:checked) .role-radio-text {
    color: #fff;
    font-weight: 600;
  }

  .theme--dark .role-radio-label,
  .dark .role-radio-label {
    background-color: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.35);
  }

  .theme--dark .role-radio-text,
  .dark .role-radio-text {
    color: rgba(255, 255, 255, 0.87);
  }

  .theme--dark .role-radio-label--active,
  .dark .role-radio-label--active,
  .theme--dark .role-radio-label:has(.role-radio-input:checked),
  .dark .role-radio-label:has(.role-radio-input:checked) {
    background-color: #7b4397;
    border-color: #7b4397;
  }
  </style>
  